/**
 *
 * PreviewPagesList
 *
 * Per-person list of the preview pages a person currently appears on.
 * Lives on the person edit page as a tab.
 *
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiRequest } from 'utils/request';
import { LoadingIndicator } from '../../components/LoadingIndicator';

interface Row {
  id: number;
  reference: string;
  description: string | null;
  active: boolean;
  public_url: string | null;
  expires_at: string | null;
  added_at: string | null;
  updated_at: string | null;
}

interface Props {
  personId: number;
}

type EffectiveStatus = 'active' | 'expired' | 'inactive';

const effectiveStatus = (row: Row): EffectiveStatus => {
  if (!row.active) return 'inactive';
  if (row.expires_at && new Date(row.expires_at) < new Date()) {
    return 'expired';
  }
  return 'active';
};

const statusChip: Record<
  EffectiveStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  active: { label: 'Active', color: 'success' },
  expired: { label: 'Expired', color: 'warning' },
  inactive: { label: 'Inactive', color: 'default' },
};

export function PreviewPagesList({ personId }: Props) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest({
          url: `people/${personId}/preview-pages`,
          method: 'get',
        });
        if (!cancelled) setRows(res.data);
      } catch (e: any) {
        if (!cancelled) setError(e?.payload?.message || 'Failed to load.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (rows === null) return <LoadingIndicator />;

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          This person is not on any preview pages.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">Preview pages ({rows.length})</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Added</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Public link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const status = effectiveStatus(row);
              const chip = statusChip[status];
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <MuiLink
                      component={RouterLink}
                      to={`/portal/preview-pages/${row.id}`}
                    >
                      {row.reference || `Page #${row.id}`}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={chip.color} label={chip.label} />
                  </TableCell>
                  <TableCell>
                    {row.added_at
                      ? new Date(row.added_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {row.expires_at
                      ? new Date(row.expires_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {status === 'active' && row.public_url ? (
                      <MuiLink
                        href={row.public_url}
                        target="_blank"
                        rel="noopener"
                      >
                        Open
                      </MuiLink>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Not public
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    </Box>
  );
}
